import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import {
    AthleteAdminBreadcrumbNav,
    AthleteAdminNavState,
    getTeamMemberBreadcrumbLabel,
    navigateToTeamMemberFromBreadcrumb,
    useAdminAthleteDisplayName,
    useAthleteStaffForAdminBreadcrumb,
} from "./AthleteAdminBreadcrumbNav";
import AthleteHomeActionBlock from "./AthleteHomeActionBlock";
import {
    getAthleteHomeActions,
    resolveAthleteHomePath,
    type AthleteHomeActionId,
} from "./athleteHomeActions";
import {
    fetchAthleteHomeCompletion,
    parseAthleteHomeContext,
    type AthleteHomeCompletionMap,
} from "./athleteHomeCompletion";
import AthleteOnboardingWizard from "./AthleteOnboardingWizard";
import {
    dismissAthleteOnboardingWizard,
    fetchAthleteOnboardingStatus,
    notifyAthleteOnboardingUpdated,
} from "../services/athleteOnboardingService";
import { getTermsAccepted, isAthlete, isGuardian } from "../utils/auth";
import { athleteProfileFetchPath, ensureGuardianViewingAthleteId, getGuardianViewingAthleteId } from "../utils/viewedAthlete";
import { buildApiUrl } from "../utils/apiConfig";
import "../styles/athlete-admin-layout.css";
import "../styles/athlete-admin-scaled-wrapper.css";
import "../styles/athlete-home.css";
import "../styles/athlete-admin-page-spacing.css";

const EMPTY_COMPLETION: AthleteHomeCompletionMap = {
    profile: false,
    ncaa: false,
    "college-list": false,
    "nil-course": false,
    "media-kit": false,
    hub: false,
    calendar: false,
};

export default function AthleteHomePage() {
    const { athleteId } = useParams<{ athleteId?: string }>();
    const location = useLocation();
    const navigate = useNavigate();
    const guardianViewingAthleteId =
        getGuardianViewingAthleteId() ?? (isGuardian() ? ensureGuardianViewingAthleteId() : null);
    const athleteView = isAthlete() || (isGuardian() && Boolean(guardianViewingAthleteId));
    const isAdminViewingAthlete = !athleteView && Boolean(athleteId);

    const [error, setError] = useState<string | null>(null);
    const [completion, setCompletion] = useState<AthleteHomeCompletionMap>(EMPTY_COMPLETION);
    const [athleteProfileId, setAthleteProfileId] = useState<number | null>(null);
    const [showOnboardingWizard, setShowOnboardingWizard] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const isAthleteSelfView = isAthlete() && !isAdminViewingAthlete;

    const navState = location.state as AthleteAdminNavState | null;
    const teamMemberName = navState?.teamMemberName;

    const { staffId: athleteStaffId, staffName: athleteStaffName } = useAthleteStaffForAdminBreadcrumb(
        athleteId,
        Boolean(isAdminViewingAthlete && athleteId),
    );

    const athleteLabel = useAdminAthleteDisplayName(athleteId, navState?.athleteDisplayName);

    const teamMemberLabel = getTeamMemberBreadcrumbLabel(
        teamMemberName ? { firstName: teamMemberName.split(" ")[0], lastName: teamMemberName.split(" ").slice(1).join(" ") } : null,
        teamMemberName,
        athleteStaffName ?? undefined,
    );

    useEffect(() => {
        let cancelled = false;

        (async () => {
            setError(null);
            setCompletion(EMPTY_COMPLETION);
            setAthleteProfileId(null);

            const profilePath = athleteProfileFetchPath(athleteId);
            const url = profilePath ? buildApiUrl(profilePath) : null;

            if (!url) {
                if (!cancelled) {
                    setError("Unable to load athlete information.");
                }
                return;
            }

            try {
                const response = await fetch(url, {
                    headers: { Accept: "application/json" },
                    credentials: "include",
                });

                if (!response.ok) {
                    // A brand-new athlete has not accepted the Terms & Conditions yet, so protected
                    // endpoints return 403 until they do. AthleteLayout renders the T&C modal for that
                    // flow and this page re-fetches in place via the `termsAccepted` event once they
                    // accept, so suppress the error here instead of a misleading "Unable to load" box.
                    const awaitingTermsAcceptance =
                        response.status === 403 && isAthleteSelfView && !getTermsAccepted();
                    if (!cancelled && !awaitingTermsAcceptance) {
                        setError("Unable to load athlete information.");
                    }
                    return;
                }

                const data = (await response.json()) as Record<string, unknown>;
                const context = parseAthleteHomeContext(data, {
                    isAdminViewingAthlete,
                    adminAthleteProfileId: athleteId,
                });

                if (!context) {
                    if (!cancelled) {
                        setError("Unable to load athlete information.");
                    }
                    return;
                }

                if (!cancelled) {
                    setAthleteProfileId(context.profileId);
                }

                const completionMap = await fetchAthleteHomeCompletion(context);
                if (!cancelled) {
                    setCompletion(completionMap);
                }
            } catch {
                if (!cancelled) {
                    setError("Unable to load athlete information.");
                }
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [athleteId, guardianViewingAthleteId, isAdminViewingAthlete, isAthleteSelfView, reloadKey]);

    // A brand-new athlete's data only loads once they accept the Terms & Conditions. Re-run the
    // load when the T&C modal reports acceptance so the home page populates without a full refresh.
    useEffect(() => {
        const handleTermsAccepted = () => setReloadKey((key) => key + 1);
        window.addEventListener("termsAccepted", handleTermsAccepted);
        return () => window.removeEventListener("termsAccepted", handleTermsAccepted);
    }, []);

    useEffect(() => {
        if (!isAthleteSelfView) {
            setShowOnboardingWizard(false);
            return;
        }

        if (localStorage.getItem("requirePasswordChange") === "true") {
            setShowOnboardingWizard(false);
            return;
        }

        let cancelled = false;
        (async () => {
            const status = await fetchAthleteOnboardingStatus();
            if (!cancelled) {
                setShowOnboardingWizard(status?.showWizard === true);
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [isAthleteSelfView, reloadKey]);

    const refreshOnboardingStatus = useCallback(async () => {
        if (!isAthleteSelfView) {
            return;
        }
        const status = await fetchAthleteOnboardingStatus();
        setShowOnboardingWizard(status?.showWizard === true);
        notifyAthleteOnboardingUpdated();
    }, [isAthleteSelfView]);

    useEffect(() => {
        if (!isAthleteSelfView) {
            return;
        }
        if (Object.values(completion).every(Boolean)) {
            void refreshOnboardingStatus();
        }
    }, [completion, isAthleteSelfView, refreshOnboardingStatus]);

    const actions = useMemo(() => getAthleteHomeActions(), []);
    const adminAthleteId = isAdminViewingAthlete ? athleteId : undefined;

    const handleActionCompleted = useCallback((actionId: AthleteHomeActionId) => {
        setCompletion((prev) => {
            const next = { ...prev, [actionId]: true };
            const allComplete = Object.values(next).every(Boolean);
            if (allComplete) {
                void refreshOnboardingStatus();
            }
            return next;
        });
    }, [refreshOnboardingStatus]);

    const handleWizardDismiss = useCallback(async () => {
        await dismissAthleteOnboardingWizard();
        setShowOnboardingWizard(false);
        notifyAthleteOnboardingUpdated();
    }, []);

    const adminBreadcrumb = isAdminViewingAthlete ? (
        <AthleteAdminBreadcrumbNav
            pageLabel="Onboarding"
            teamMemberLabel={teamMemberLabel}
            onTeamMemberClick={() => navigateToTeamMemberFromBreadcrumb(navigate, navState?.teamMemberId, athleteStaffId)}
            athleteLabel={athleteLabel}
            athleteProfileId={athleteId}
            data-testid="athlete-home-breadcrumb"
        />
    ) : null;

    if (error) {
        return (
            <div className={`athlete-home-wrapper ${isAdminViewingAthlete ? "athlete-admin-view" : ""}`} data-testid="athlete-home-page">
                {adminBreadcrumb}
                <div className="athlete-home-container">
                    <div className="athlete-home-error" data-testid="athlete-home-error">{error}</div>
                </div>
            </div>
        );
    }

    return (
        <div className={`athlete-home-wrapper ${isAdminViewingAthlete ? "athlete-admin-view" : ""}`} data-testid="athlete-home-page">
            {adminBreadcrumb}
            <div className={`athlete-home-container ${isAdminViewingAthlete ? "with-athlete-sidebar" : ""}`}>
                <div className="athlete-home-action-list" data-testid="athlete-home-action-list">
                    {actions.map((action) => (
                        <AthleteHomeActionBlock
                            key={action.id}
                            action={action}
                            destination={resolveAthleteHomePath(action.destination, adminAthleteId)}
                            completed={completion[action.id as AthleteHomeActionId]}
                            athleteProfileId={athleteProfileId}
                            onActionCompleted={handleActionCompleted}
                        />
                    ))}
                </div>
            </div>
            {isAthleteSelfView && (
                <AthleteOnboardingWizard open={showOnboardingWizard} onDismiss={handleWizardDismiss} />
            )}
        </div>
    );
}
